'use client';

import { ArrowRight, ChevronLeft, ChevronRight, X, Info, ClipboardList, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSession } from 'next-auth/react';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { z } from 'zod';
import { cn } from '@/lib/utils';

import { Button } from '@/components/common/inputs/button/button';
import { Input } from '@/components/common/inputs/text/input';
import { Textarea } from '@/components/common/inputs/textarea/textarea';
import { Label } from '@/components/common/inputs/text/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/common/inputs/radio/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/inputs/select/select';
import { useFormStore } from '@/lib/hooks/useFormStore';
import { useI18n, useScopedI18n } from '@/locales/client';

// --------------------------------------------------------------------------------
// 1. VALIDATION SCHEMA (Removed leaseType; user must choose everything manually)
// --------------------------------------------------------------------------------
const formDataSchema = z.object({
  userId: z.string().uuid('Invalid user ID'),
  userType: z.string().min(1, 'User type is required'),
  excludedSituation: z.string().min(1, 'Excluded situation is required'),
  propertyOwnership: z.boolean().optional(),
  singleOwnership: z.boolean().optional(),
  spouseOwnership: z.boolean().optional(),
  leaseTermType: z.string().min(1, 'Lease term type is required'),
  leaseDuration: z.number().optional(),
  buildingType: z.string().min(1, 'Building type is required'),
  buildingAgeUnder5: z.string().min(1, 'Building age is required'),
  tenant65Over: z.boolean().nullable(),
  additionalInfo: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export type FormData = z.infer<typeof formDataSchema>;

// Each step's config: includes content rendering & completion check
interface StepConfig {
  title: string;
  content: React.ReactNode;
  isStepComplete: () => boolean;
  skipStep?: () => boolean;
}

// --------------------------------------------------------------------------------
// 3. COMPONENT
// --------------------------------------------------------------------------------
export default function Questionnaire() {
  const { data: session, update } = useSession();
  const t = useI18n();
  const tForm = useScopedI18n('formQuestionnaire');

  const {
    formData: savedFormData,
    formAnswerId,
    isSubmitting,
    isQuestionnaireSubmitted,
    questionnaireSubmission,
    submitForm,
    updateForm,
    setQuestionnaireSubmitted: setSubmitted,
    setQuestionnaireSubmission: setSubmission,
  } = useFormStore();

  // ------------------------
  // State
  // ------------------------
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0); // -1 for previous, 1 for next
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isSkipping, setIsSkipping] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // For excluded-situation feedback
  const [isExcludedSituationModalOpen, setIsExcludedSituationModalOpen] =
    useState(false);
  const [excludedSituationFeedback, setExcludedSituationFeedback] =
    useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const defaultFormData: FormData = useMemo(
    () => ({
      userId: session?.user?.id || '',
      userType: '',
      excludedSituation: '',
      propertyOwnership: undefined,
      singleOwnership: undefined,
      spouseOwnership: undefined,
      leaseTermType: '',
      leaseDuration: undefined,
      buildingType: '',
      buildingAgeUnder5: '',
      tenant65Over: null,
      additionalInfo: '',
      firstName: '',
      lastName: '',
    }),
    [session?.user?.id]
  );

  const [formData, setFormData] = useState<FormData>(defaultFormData);

  // --------------------------------------------------------------------------------
  // 4. FETCH EXISTING FORM DATA
  // --------------------------------------------------------------------------------
  useEffect(() => {
    if (session?.user?.id) {
      setFormData((prevData) => ({
        ...prevData,
        userId: session.user.id,
      }));
    }
  }, [session?.user?.id]);

  // Update formData when savedFormData changes
  useEffect(() => {
    if (savedFormData?.answers) {
      setFormData((prevData) => ({
        ...prevData,
        ...savedFormData.answers,
        // Ensure optional fields have proper fallbacks
        additionalInfo: savedFormData.answers.additionalInfo || '',
        propertyOwnership:
          savedFormData.answers.propertyOwnership ?? prevData.propertyOwnership,
        singleOwnership:
          savedFormData.answers.singleOwnership ?? prevData.singleOwnership,
        spouseOwnership:
          savedFormData.answers.spouseOwnership ?? prevData.spouseOwnership,
        leaseDuration:
          savedFormData.answers.leaseDuration ?? prevData.leaseDuration,
      }));

      // If we have complete data and it's not excluded, update submission status
      if (
        savedFormData.answers.userType &&
        savedFormData.answers.excludedSituation &&
        savedFormData.answers.excludedSituation !== 'none'
      ) {
        setSubmission('complete');
        setSubmitted(true);
      }
    } else {
      // Reset to default state if no saved form data
      setFormData(defaultFormData);
      setSubmission('none');
      setSubmitted(false);
    }
  }, [savedFormData, setSubmission, setSubmitted, defaultFormData]);

  // useEffect at the top level of your component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        openTooltip &&
        tooltipRefs.current[openTooltip] &&
        !tooltipRefs.current[openTooltip]!.contains(event.target as Node)
      ) {
        setOpenTooltip(null);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openTooltip]);

  // --------------------------------------------------------------------------------
  // 5. VALIDATION
  // --------------------------------------------------------------------------------
  const validateFormData = (data: FormData): boolean => {
    try {
      formDataSchema.parse(data);
      setValidationErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            errors[err.path[0]] = err.message;
          }
        });
        setValidationErrors(errors);
        toast.error('Please fill in all required fields');
      }
      return false;
    }
  };

  // --------------------------------------------------------------------------------
  // 6. FORM SUBMISSION
  // --------------------------------------------------------------------------------
  const handleSubmit = async (data: FormData) => {
    if (!session?.user?.id || !session?.user?.accessToken) {
      toast.error('Please log in to continue');
      return;
    }

    // Validate entire form before final submission
    if (!validateFormData(data)) return;

    try {
      const formDataWithUser = { ...data, userId: session.user.id };
      let success = false;

      if (!formAnswerId) {
        // Create new
        const response = await submitForm(formDataWithUser);
        success = !!response;
      } else {
        // Update existing
        try {
          success = await updateForm(formDataWithUser, formAnswerId);
        } catch (error: any) {
          if (error.response?.data?.code === 'FORM_ACCESS_DENIED') {
            toast.error('You do not have permission to update this form');
            return;
          }
          toast.error('Failed to update form');
          return;
        }
      }

      // if (success) {
      //   await update(); // Trigger session update
      // }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form');
    }
  };

  // Skip logic: sets form to default values (or partial defaults) and marks submitted
  const handleSkip = async () => {
    if (!session?.user?.id || !session?.user?.accessToken) {
      toast.error('Please log in to continue');
      return;
    }

    setIsSkipping(true);
    try {
      const skipFormData = {
        ...defaultFormData,
        userId: session.user.id,
        userType: 'tenant',
        excludedSituation: 'none',
        leaseTermType: 'undetermined',
        buildingType: 'none',
        buildingAgeUnder5: 'dont-know',
        tenant65Over: false,
        additionalInfo: '',
      };

      // Only call API if form was never submitted before
      if (!formAnswerId && !savedFormData) {
        await submitForm(skipFormData);
      } else {
        // If form was already submitted, just update local state
        setSubmission('skipped');
        setSubmitted(true);
      }

      // await update();
    } catch (error) {
      console.error('Error skipping form:', error);
      toast.error('Failed to skip questionnaire');
    } finally {
      setIsSkipping(false);
    }
  };

  // --------------------------------------------------------------------------------
  // 7. STEPS
  // --------------------------------------------------------------------------------
  const steps: StepConfig[] = [
    // -------------------------------
    // Step 0: Intro
    // -------------------------------
    {
      title: '',
      content: (
        <div className='flex flex-col items-center justify-center space-y-8 py-10 text-center'>
          <div className='flex h-20 w-20 items-center justify-center rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'>
            <ClipboardList className='h-10 w-10' />
          </div>
          <div className='mx-auto space-y-4'>
            <h3 className='mx-auto max-w-lg text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100'>
              {t('home.description')}
            </h3>
            <p className='mx-auto text-lg text-gray-500 dark:text-gray-400'>
              {tForm('intro.description')}
            </p>
          </div>
        </div>
      ),
      isStepComplete: () => true,
    },

    // -------------------------------
    // Step 1: Excluded Situation
    // -------------------------------
    {
      title: tForm('q8.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-sm text-gray-700 dark:text-gray-300'>
            {tForm('q8.question')}
          </Label>
          <div className='mt-2 space-y-3'>
            {[
              {
                id: 'vacation-rental',
                value: 'vacation-rental',
                text: tForm('q8.exceptions.e1'),
                // definition: tForm('q8.definitions.e1'), // add definition here
              },
              {
                id: 'non-residential',
                value: 'non-residential',
                text: tForm('q8.exceptions.e2'),
                // definition: tForm('q8.definitions.e2'),
              },
              {
                id: 'hotel-room',
                value: 'hotel-room',
                text: tForm('q8.exceptions.e3'),
                // definition: tForm('q8.definitions.e3'),
              },
              {
                id: 'landlord-primary-residence',
                value: 'landlord-primary-residence',
                text: tForm('q8.exceptions.e4'),
                definition:
                  'Le bail d’une chambre située dans la résidence principale du locateur, lorsque deux chambres au maximum y sont louées ou offertes en location et que la chambre ne possède ni sortie distincte donnant sur l’extérieur ni installations sanitaires indépendantes de celles utilisées par le locateur (C.C.Q 1892)',
              },
              {
                id: 'healthcare-facility',
                value: 'healthcare-facility',
                text: tForm('q8.exceptions.e5'),
                definition: `Le bail d’une chambre située dans un établissement de santé et de services sociaux, sauf en application de l’article 1974\n`,
              },
              {
                id: 'none',
                value: 'none',
                text: tForm('q8.exceptions.e6'),
                // definition: tForm('q8.definitions.e6'),
              },
            ].map((option) => (
              <div
                key={option.id}
                className={cn(
                  'group relative flex flex-row items-center justify-between gap-3 rounded-xl border-2 p-4 transition-all md:gap-4',
                  formData.excludedSituation === option.value
                    ? 'border-purple-600 bg-purple-50 shadow-sm dark:border-purple-500 dark:bg-purple-900/20'
                    : 'border-gray-100 bg-white hover:border-purple-300 hover:bg-purple-50/30 dark:border-gray-800 dark:bg-gray-900/50 dark:hover:border-purple-700 dark:hover:bg-purple-900/10'
                )}
              >
                <div className='flex min-w-0 flex-1 items-center space-x-3'>
                  <RadioGroup
                    value={formData.excludedSituation}
                    onValueChange={(val) =>
                      setFormData({ ...formData, excludedSituation: val })
                    }
                  >
                    <div className='flex items-center space-x-3'>
                      <RadioGroupItem
                        value={option.value}
                        id={option.id}
                        className='h-4 w-4 shrink-0'
                      />
                      <Label
                        htmlFor={option.id}
                        className='cursor-pointer break-words text-sm font-medium text-gray-700 dark:text-gray-300'
                      >
                        {option.text}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {option.definition && (
                  <div
                    className='relative'
                    ref={(el) => {
                      tooltipRefs.current[option.id] = el;
                    }}
                  >
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() =>
                        setOpenTooltip(
                          openTooltip === option.id ? null : option.id
                        )
                      }
                      className='h-8 w-8 text-gray-400 hover:text-purple-600 dark:text-gray-500 dark:hover:text-purple-400'
                    >
                      <Info className='h-4 w-4' />
                    </Button>
                    <AnimatePresence>
                      {openTooltip === option.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className='absolute bottom-full right-0 z-20 mb-3 w-72 rounded-xl bg-gray-900 p-4 text-xs leading-relaxed text-white shadow-xl dark:bg-gray-800'
                        >
                          <div className='absolute -bottom-1.5 right-3 h-3 w-3 rotate-45 bg-gray-900 dark:bg-gray-800' />
                          {option.definition}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ),
      isStepComplete: () => !!formData.excludedSituation,
    },

    // -------------------------------
    // Step 2: Building Type
    // -------------------------------
    {
      title: tForm('q4.title'),
      content: (
        <div className='space-y-6'>
          <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
            {tForm('q4.question')}
          </Label>
          <RadioGroup
            value={formData.buildingType}
            onValueChange={(value) =>
              setFormData({ ...formData, buildingType: value })
            }
            className='grid grid-cols-1 gap-4 sm:grid-cols-2'
          >
            {[
              { id: 'llm', value: 'llm', text: tForm('q4.option1') },
              {
                id: 'university',
                value: 'university',
                text: tForm('q4.option2'),
              },
              {
                id: 'mobile-home',
                value: 'mobile-home',
                text: tForm('q4.option3'),
              },
              { id: 'none', value: 'none', text: tForm('q4.option4') },
            ].map((option) => (
              <Label
                key={option.id}
                htmlFor={option.id}
                className={cn(
                  'flex cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                  formData.buildingType === option.value
                    ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                    : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                )}
              >
                <RadioGroupItem
                  value={option.value}
                  id={option.id}
                  className='h-4 w-4 shrink-0'
                />
                <span className='font-medium text-gray-900 dark:text-gray-100'>
                  {option.text}
                </span>
              </Label>
            ))}
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => !!formData.buildingType,
    },

    // -------------------------------
    // Step 3: User Type
    // -------------------------------
    {
      title: tForm('q1.title'),
      content: (
        <div className='space-y-4'>
          <Label className='text-gray-700 dark:text-gray-300'>
            {tForm('q1.question')}
          </Label>
          <RadioGroup
            value={formData.userType}
            onValueChange={(value) => {
              setFormData({
                ...formData,
                userType: value,
                propertyOwnership: undefined,
                singleOwnership: undefined,
                spouseOwnership: undefined,
              });
            }}
            className='grid grid-cols-1 gap-4 sm:grid-cols-2'
          >
            {[
              { id: 'landlord', value: 'landlord', text: tForm('q1.option1') },
              { id: 'tenant', value: 'tenant', text: tForm('q1.option2') },
              {
                id: 'sub-tenant',
                value: 'sub-tenant',
                text: tForm('q1.option3'),
              },
              { id: 'occupant', value: 'occupant', text: tForm('q1.option4') },
            ].map((option) => (
              <Label
                key={option.id}
                htmlFor={option.id}
                className={cn(
                  'flex cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                  formData.userType === option.value
                    ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                    : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                )}
              >
                <RadioGroupItem
                  value={option.value}
                  id={option.id}
                  className='h-4 w-4 shrink-0'
                />
                <span className='font-medium text-gray-900 dark:text-gray-100'>
                  {option.text}
                </span>
              </Label>
            ))}
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => !!formData.userType,
    },

    // -------------------------------
    // Step 4: Ownership details (Landlord only)
    // -------------------------------
    {
      title: tForm('q2.title'),
      content:
        formData.userType === 'landlord' ? (
          <div className='space-y-6'>
            <div className='space-y-4'>
              <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
                {tForm('q2.part1.question')}
              </Label>
              <RadioGroup
                value={String(formData.propertyOwnership)}
                onValueChange={(value) => {
                  setFormData({
                    ...formData,
                    propertyOwnership: value === 'true',
                    singleOwnership: undefined,
                    spouseOwnership: undefined,
                  });
                }}
                className='flex flex-col gap-4 sm:flex-row'
              >
                {[
                  { value: 'true', id: 'ownership-yes', text: tForm('q2.part1.option1') },
                  { value: 'false', id: 'ownership-no', text: tForm('q2.part1.option2') },
                ].map((option) => (
                  <Label
                    key={option.id}
                    htmlFor={option.id}
                    className={cn(
                      'flex flex-1 cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                      String(formData.propertyOwnership) === option.value
                        ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                        : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                    )}
                  >
                    <RadioGroupItem value={option.value} id={option.id} className='h-4 w-4 shrink-0' />
                    <span className='font-medium text-gray-900 dark:text-gray-100'>{option.text}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            {formData.propertyOwnership === true && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className='mt-6 space-y-4 border-t pt-6 dark:border-gray-800'
              >
                <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
                  {tForm('q2.part2.question')}
                </Label>
                <RadioGroup
                  value={String(formData.singleOwnership)}
                  onValueChange={(value) => {
                    setFormData({
                      ...formData,
                      singleOwnership: value === 'true',
                      spouseOwnership: undefined,
                    });
                  }}
                  className='flex flex-col gap-4 sm:flex-row'
                >
                  {[
                    { value: 'true', id: 'sole-owner', text: tForm('q2.part2.option1') },
                    { value: 'false', id: 'not-sole-owner', text: tForm('q2.part2.option2') },
                  ].map((option) => (
                    <Label
                      key={option.id}
                      htmlFor={option.id}
                      className={cn(
                        'flex flex-1 cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                        String(formData.singleOwnership) === option.value
                          ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                          : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                      )}
                    >
                      <RadioGroupItem value={option.value} id={option.id} className='h-4 w-4 shrink-0' />
                      <span className='font-medium text-gray-900 dark:text-gray-100'>{option.text}</span>
                    </Label>
                  ))}
                </RadioGroup>

                {formData.singleOwnership === false && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className='mt-6 space-y-4 border-t pt-6 dark:border-gray-800'
                  >
                    <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
                      {tForm('q2.part3.question')}
                    </Label>
                    <RadioGroup
                      value={String(formData.spouseOwnership)}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          spouseOwnership: value === 'true',
                        })
                      }
                      className='flex flex-col gap-4 sm:flex-row'
                    >
                      {[
                        { value: 'true', id: 'yes-spouse', text: tForm('q2.part3.option1') },
                        { value: 'false', id: 'no-spouse', text: tForm('q2.part3.option2') },
                      ].map((option) => (
                        <Label
                          key={option.id}
                          htmlFor={option.id}
                          className={cn(
                            'flex flex-1 cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                            String(formData.spouseOwnership) === option.value
                              ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                              : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                          )}
                        >
                          <RadioGroupItem value={option.value} id={option.id} className='h-4 w-4 shrink-0' />
                          <span className='font-medium text-gray-900 dark:text-gray-100'>{option.text}</span>
                        </Label>
                      ))}
                    </RadioGroup>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        ) : null,
      isStepComplete: () =>
        formData.userType !== 'landlord' ||
        (formData.propertyOwnership !== undefined &&
          (formData.propertyOwnership === false ||
            formData.singleOwnership === true ||
            (formData.singleOwnership === false &&
              formData.spouseOwnership !== undefined))),
      skipStep: () => formData.userType !== 'landlord',
    },

    // -------------------------------
    // Step 5: Lease Term
    // -------------------------------
    {
      title: tForm('q3.title'),
      content: (
        <div className='space-y-8'>
          {/* Lease Term Type */}
          <div className='space-y-4'>
            <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
              {tForm('q3.part2.question')}
            </Label>
            <RadioGroup
              value={formData.leaseTermType}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  leaseTermType: value,
                  leaseDuration:
                    value === 'fixed-term' ? formData.leaseDuration : undefined,
                })
              }
              className='grid grid-cols-1 gap-4 sm:grid-cols-2'
            >
              {[
                { value: 'fixed-term', id: 'fixed-term', text: tForm('q3.part2.option1') },
                { value: 'undetermined', id: 'undetermined', text: tForm('q3.part2.option2') },
              ].map((option) => (
                <Label
                  key={option.id}
                  htmlFor={option.id}
                  className={cn(
                    'flex cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                    formData.leaseTermType === option.value
                      ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                      : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                  )}
                >
                  <RadioGroupItem value={option.value} id={option.id} className='h-4 w-4 shrink-0' />
                  <span className='font-medium text-gray-900 dark:text-gray-100'>{option.text}</span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          {/* Lease Duration if fixed-term */}
          {formData.leaseTermType === 'fixed-term' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className='space-y-4 border-t pt-6 dark:border-gray-800'
            >
              <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
                {tForm('q3.part3.question')}
              </Label>
              <div className='relative max-w-[250px]'>
                <Input
                  type='number'
                  value={formData.leaseDuration || ''}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseInt(e.target.value, 10)
                      : undefined;
                    setFormData({
                      ...formData,
                      leaseDuration: value,
                    });
                  }}
                  placeholder={tForm('q3.part3.placeholder')}
                  className='h-12 w-full pr-16 text-lg'
                  min={1}
                />
                <div className='absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400'>
                  {tForm('viewAnswers.months')}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      ),
      isStepComplete: () =>
        !!formData.leaseTermType &&
        (formData.leaseTermType !== 'fixed-term' || !!formData.leaseDuration),
    },

    // -------------------------------
    // Step 6: Tenant 65+
    // -------------------------------
    {
      title: tForm('q5.title'),
      content: (
        <div className='space-y-6'>
          <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
            {tForm('q5.question')}
          </Label>
          <RadioGroup
            value={
              formData.tenant65Over !== null
                ? String(formData.tenant65Over)
                : ''
            }
            onValueChange={(value) =>
              setFormData({ ...formData, tenant65Over: value === 'true' })
            }
            className='grid grid-cols-1 gap-4 sm:grid-cols-2'
          >
            {[
              { value: 'true', id: 'over-65', text: tForm('q5.option1') },
              { value: 'false', id: 'under-65', text: tForm('q5.option2') },
            ].map((option) => (
              <Label
                key={option.id}
                htmlFor={option.id}
                className={cn(
                  'flex cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                  formData.tenant65Over !== null && String(formData.tenant65Over) === option.value
                    ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                    : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                )}
              >
                <RadioGroupItem value={option.value} id={option.id} className='h-4 w-4 shrink-0' />
                <span className='font-medium text-gray-900 dark:text-gray-100'>{option.text}</span>
              </Label>
            ))}
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => formData.tenant65Over !== null,
    },

    // -------------------------------
    // Step 7: Building Age
    // -------------------------------
    {
      title: tForm('q6.title'),
      content: (
        <div className='space-y-6'>
          <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
            {tForm('q6.question')}
          </Label>
          <RadioGroup
            value={formData.buildingAgeUnder5}
            onValueChange={(value) =>
              setFormData({ ...formData, buildingAgeUnder5: value })
            }
            className='grid grid-cols-1 gap-4'
          >
            {[
              { value: 'under-5', id: 'under-5', text: tForm('q6.option1') },
              { value: 'over-5', id: 'over-5', text: tForm('q6.option2') },
              { value: 'dont-know', id: 'dont-know', text: tForm('q6.option3') },
            ].map((option) => (
              <Label
                key={option.id}
                htmlFor={option.id}
                className={cn(
                  'flex cursor-pointer items-center space-x-3 rounded-xl border-2 p-4 transition-all hover:border-purple-300 hover:bg-purple-50/30 dark:hover:border-purple-700 dark:hover:bg-purple-900/10',
                  formData.buildingAgeUnder5 === option.value
                    ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/20'
                    : 'border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50'
                )}
              >
                <RadioGroupItem value={option.value} id={option.id} className='h-4 w-4 shrink-0' />
                <span className='font-medium text-gray-900 dark:text-gray-100'>{option.text}</span>
              </Label>
            ))}
          </RadioGroup>
        </div>
      ),
      isStepComplete: () => !!formData.buildingAgeUnder5,
    },

    // -------------------------------
    // Step 8: Additional Info
    // -------------------------------
    {
      title: tForm('q7.title'),
      content: (
        <div className='space-y-6'>
          <Label className='text-lg font-medium text-gray-900 dark:text-gray-100'>
            {tForm('q7.question')}
          </Label>
          <Textarea
            className='mt-2 min-h-[150px] w-full rounded-xl border-2 p-4 text-lg transition-all focus:border-purple-600 dark:bg-gray-900/50'
            placeholder={tForm('q7.placeholder')}
            value={formData.additionalInfo || ''}
            onChange={(e) =>
              setFormData({ ...formData, additionalInfo: e.target.value })
            }
            rows={6}
          />
        </div>
      ),
      isStepComplete: () => true,
    },
  ];

  // --------------------------------------------------------------------------------
  // 8. STEP NAVIGATION
  // --------------------------------------------------------------------------------
  const handleNext = () => {
    // If we are on step 1 (excludedSituation) and the user selected anything except 'none',
    // we open the excluded-situation modal and do NOT proceed to the next step
    if (step === 1 && formData.excludedSituation !== 'none') {
      setIsExcludedSituationModalOpen(true);
      return; // Stop here
    }

    let nextStep = step + 1;
    // Skip logic for landlord steps if not landlord, etc.
    while (nextStep < steps.length) {
      const skipCheck = steps[nextStep].skipStep;
      if (skipCheck && skipCheck()) {
        nextStep++;
      } else {
        break;
      }
    }
    if (nextStep < steps.length) {
      setDirection(1);
      setStep(nextStep);
    }
  };

  const handlePrevious = () => {
    let prevStep = step - 1;
    while (prevStep >= 0) {
      const skipCheck = steps[prevStep].skipStep;
      if (skipCheck && skipCheck()) {
        prevStep--;
      } else {
        break;
      }
    }
    if (prevStep >= 0) {
      setDirection(-1);
      setStep(prevStep);
    }
  };

  const isStepComplete = steps[step].isStepComplete();

  // --------------------------------------------------------------------------------
  // 9. EXCLUDED-SITUATION MODAL LOGIC
  // --------------------------------------------------------------------------------
  const handleSubmitExcludedFeedback = () => {
    if (excludedSituationFeedback.trim() === '') {
      // optional: show a toast or something that feedback is empty
      setIsExcludedSituationModalOpen(false);
      setStep(0);
      setFormData(defaultFormData);
      return;
    }
    // For now, we simply close the modal, reset to step 0, and show a success msg
    setIsExcludedSituationModalOpen(false);
    setStep(0);
    setFormData(defaultFormData);

    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
    setExcludedSituationFeedback('');
  };

  // --------------------------------------------------------------------------------
  // 10. RENDER
  // --------------------------------------------------------------------------------
  return (
    <>
      {!isQuestionnaireSubmitted && (
        <div className='mx-auto w-full max-w-[800px] overflow-hidden rounded-xl bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6 md:p-8'>
          {/* Progress Bar */}
          <div className='mb-6 flex items-center space-x-2'>
            <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700'>
              <motion.div
                initial={false}
                animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
                className='h-full bg-purple-600'
                transition={{ duration: 0.3, ease: 'easeOut' }}
              />
            </div>
            <span className='text-xs font-medium text-gray-400 dark:text-gray-500'>
              {step + 1} / {steps.length}
            </span>
          </div>

          <AnimatePresence mode='wait' custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={{
                enter: (direction: number) => ({
                  x: direction > 0 ? 50 : -50,
                  opacity: 0,
                }),
                center: {
                  zIndex: 1,
                  x: 0,
                  opacity: 1,
                },
                exit: (direction: number) => ({
                  zIndex: 0,
                  x: direction < 0 ? 50 : -50,
                  opacity: 0,
                }),
              }}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
            >
              <h2 className='mb-6 text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl'>
                {steps[step].title}
              </h2>

              <div className='mb-8 min-h-[300px]'>{steps[step].content}</div>
            </motion.div>
          </AnimatePresence>

          <div className='flex flex-col justify-between space-y-4 sm:flex-row sm:space-y-0'>
            {/* Prev Button */}
            <div className='self-start'>
              {step > 0 && (
                <Button
                  variant='outline'
                  onClick={handlePrevious}
                  className='w-full sm:w-auto'
                >
                  <ChevronLeft
                    className='text-black dark:text-white'
                    strokeWidth={3}
                  />
                  <span className='ml-1'>{tForm('buttons.previous')}</span>
                </Button>
              )}
            </div>

            <div className='flex space-x-2 self-end sm:self-auto'>
              {/* Next or Submit Button */}
              {step < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepComplete}
                  className='w-full bg-purple-600 font-medium text-white hover:bg-purple-700 sm:w-auto'
                >
                  {step === 0
                    ? questionnaireSubmission !== 'none'
                      ? tForm('buttons.edit')
                      : tForm('buttons.begin')
                    : tForm('buttons.next')}
                  <ChevronRight className='ml-1 text-white' strokeWidth={3} />
                </Button>
              ) : (
                <Button
                  onClick={() => handleSubmit(formData)}
                  disabled={!isStepComplete || isSubmitting}
                  className='w-full bg-purple-600 font-medium text-white hover:bg-purple-700 sm:w-auto'
                >
                  {isSubmitting ? (
                    <div className='flex items-center'>
                      <div className='mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white dark:border-purple-400/20 dark:border-t-purple-300' />
                      {tForm('buttons.submitting')}
                    </div>
                  ) : (
                    tForm('buttons.submit')
                  )}
                </Button>
              )}

              {/* Skip Button */}
              <button
                className='rounded border-2 border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700'
                onClick={() => setIsModalOpen(true)}
              >
                {tForm('buttons.skip')}
              </button>
            </div>
          </div>

          {/* Skip Modal */}
          <AnimatePresence>
            {isModalOpen && (
              <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsModalOpen(false)}
                  className='absolute inset-0 bg-gray-500/20 backdrop-blur-sm dark:bg-gray-900/40'
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  className='relative w-full max-w-[500px] overflow-hidden rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800 sm:p-8'
                >
                  {questionnaireSubmission === 'complete' ? (
                    <div className='space-y-6'>
                      <div className='space-y-2'>
                        <h3 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
                          {tForm('messages.alreadySubmitted.p1')}
                        </h3>
                        <p className='text-gray-500 dark:text-gray-400'>
                          {tForm('messages.alreadySubmitted.p2')}
                        </p>
                      </div>
                      <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
                        <Button
                          variant='ghost'
                          onClick={() => {
                            setIsModalOpen(false);
                            setSubmitted(true);
                          }}
                          className='text-gray-500 dark:text-gray-400'
                        >
                          {tForm('buttons.keepPrevious')}
                        </Button>
                        <Button
                          onClick={() => setIsModalOpen(false)}
                          className='bg-purple-600 font-semibold text-white hover:bg-purple-700'
                        >
                          {tForm('buttons.refill')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className='space-y-6'>
                      <div className='space-y-4'>
                        <h3 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
                          {tForm('messages.defaultSettings.warning')}
                        </h3>
                        <ul className='space-y-3 rounded-xl bg-gray-50 p-4 dark:bg-gray-900/50'>
                          {[
                            'settings.p1',
                            'settings.p2',
                            'settings.p3',
                            'settings.p4',
                          ].map((item, index) => (
                            <li key={index} className='flex items-start text-sm text-gray-600 dark:text-gray-400'>
                              <div className='mr-2 mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-500' />
                              <Markdown remarkPlugins={[remarkGfm]}>
                                {tForm(`messages.defaultSettings.${item}` as any)}
                              </Markdown>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
                        <Button
                          variant='ghost'
                          onClick={() => setIsModalOpen(false)}
                          className='text-gray-500 dark:text-gray-400'
                        >
                          {tForm('buttons.cancel')}
                        </Button>
                        <Button
                          onClick={() => {
                            setIsModalOpen(false);
                            handleSkip();
                          }}
                          className='bg-purple-600 font-semibold text-white hover:bg-purple-700'
                        >
                          {tForm('buttons.skipAnyway')}
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Success Message */}
      <AnimatePresence>
        {showSuccessMessage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            className='fixed bottom-6 right-6 z-[100] max-w-[90%] overflow-hidden rounded-2xl bg-white p-5 shadow-2xl dark:bg-gray-800 sm:max-w-md'
          >
            <div className='flex items-start space-x-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'>
                <ShieldCheck className='h-6 w-6' />
              </div>
              <div className='flex-1 pr-6 text-left'>
                <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                  {tForm('messages.feedbackSubmitted' as any)}
                </p>
              </div>
              <button
                className='text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200'
                onClick={() => setShowSuccessMessage(false)}
              >
                <X size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Excluded Situation Modal */}
      <AnimatePresence>
        {isExcludedSituationModalOpen && (
          <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsExcludedSituationModalOpen(false)}
              className='absolute inset-0 bg-gray-500/20 backdrop-blur-sm dark:bg-gray-900/40'
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className='relative w-full max-w-[500px] overflow-hidden rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800 sm:p-8'
            >
              <div className='space-y-6'>
                <div className='space-y-2'>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
                    {tForm('messages.excludedSituation.title' as any)}
                  </h3>
                  <div className='space-y-4'>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      {tForm('messages.excludedSituation.description1' as any)}
                    </p>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      {tForm('messages.excludedSituation.description2' as any)}
                    </p>
                  </div>
                </div>

                <Textarea
                  className='min-h-[120px] rounded-xl border-2 p-4 text-base transition-all focus:border-purple-600 dark:bg-gray-900/50'
                  rows={4}
                  value={excludedSituationFeedback}
                  onChange={(e) => setExcludedSituationFeedback(e.target.value)}
                  placeholder={tForm('messages.excludedSituation.placeholder' as any)}
                />

                <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
                  <Button
                    variant='ghost'
                    onClick={() => setIsExcludedSituationModalOpen(false)}
                    className='text-gray-500 dark:text-gray-400'
                  >
                    {tForm('buttons.cancel')}
                  </Button>
                  <Button
                    onClick={handleSubmitExcludedFeedback}
                    className='bg-purple-600 font-semibold text-white hover:bg-purple-700'
                  >
                    {tForm('buttons.submit')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
